// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Language detection + local persistence. The language preference is NOT
// sensitive (the server never sees it), so — unlike ledger data — it lives in
// plain localStorage. This lets it load instantly, before the encrypted event
// log is decrypted at unlock. After unlock the synced value from the settings
// event wins (see hooks/useLanguage.ts).

import { SUPPORTED_LOCALE_CODES, DEFAULT_LOCALE, getLocaleScript, getLocaleMeta, type LocaleScript } from './locales.js';

// Matches the hushold_* localStorage convention (see lib/session.ts). Non-secret.
export const LANGUAGE_STORAGE_KEY = 'hushold_language';

/**
 * Language handed over from the marketing landing page (hushold.app) as a URL
 * fragment, e.g. https://app.hushold.app/register#lang=de — the localized
 * landing pages bake it into their links to the app. A fragment (not a query
 * string, not a cookie) is deliberate: fragments never leave the browser, so
 * the preference is not sent to any server and no cookie is ever set. This
 * keeps the "no cookies at all" guarantee intact on both sites.
 */
function getFragmentLanguage(): string | null {
  if (typeof location === 'undefined') return null;
  const match = location.hash.match(/[#&]lang=([\w-]+)/);
  return match ? matchSupportedLocale(match[1]) : null;
}

/**
 * Resolve a requested code (e.g. a navigator.language like "de-AT" or "zh-TW")
 * to a supported locale, or null if none matches. Tries exact match, then a
 * base-language match, then a script-aware fallback for Chinese.
 */
export function matchSupportedLocale(requested: string | undefined | null): string | null {
  if (!requested) return null;
  const want = requested.trim();
  if (!want) return null;

  // Exact (case-insensitive) match.
  const exact = SUPPORTED_LOCALE_CODES.find((c) => c.toLowerCase() === want.toLowerCase());
  if (exact) return exact;

  const lower = want.toLowerCase();
  const base = lower.split('-')[0];

  // Chinese needs script disambiguation: TW/HK/Hant → Traditional, else Simplified.
  if (base === 'zh') {
    return /hant|tw|hk|mo/.test(lower) ? 'zh-Hant' : 'zh-Hans';
  }
  // Portuguese splits by orthography: Portugal and the African/Asian lusophone
  // countries follow European Portuguese; bare `pt` and everything else → pt-BR
  // (the larger user base; Portuguese browsers send pt-PT explicitly).
  if (base === 'pt') {
    return /-(pt|ao|mz|gw|cv|st|tl)$/.test(lower) ? 'pt-PT' : 'pt-BR';
  }
  // nb/no → Norwegian Bokmål.
  if (base === 'nb' || base === 'no') return 'nb';

  // Base-language match against the region-less codes we ship.
  const byBase = SUPPORTED_LOCALE_CODES.find((c) => c.toLowerCase() === base);
  if (byBase) return byBase;

  return null;
}

/** The user's explicitly saved choice, if any and still supported. */
export function getStoredLanguage(): string | null {
  try {
    const raw = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return raw ? matchSupportedLocale(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredLanguage(code: string): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // Storage can be unavailable (private mode); the choice just won't persist.
  }
}

/**
 * Best initial language, before any encrypted settings are available:
 *   landing-page #lang fragment → stored explicit choice → navigator.language(s)
 *   → English.
 * The fragment is checked first: it is only present when the user just arrived
 * from a localized landing page, which makes it the freshest signal. It is
 * persisted like a dropdown selection would be, because the router redirect
 * (/ → /login) strips the fragment immediately — without persisting, the very
 * first page refresh would silently fall back to browser-language detection.
 */
export function resolveInitialLanguage(): string {
  const fromLanding = getFragmentLanguage();
  if (fromLanding) {
    setStoredLanguage(fromLanding);
    return fromLanding;
  }

  const stored = getStoredLanguage();
  if (stored) return stored;

  const candidates: string[] =
    typeof navigator !== 'undefined'
      ? [...(navigator.languages ?? []), navigator.language].filter(Boolean)
      : [];
  for (const c of candidates) {
    const match = matchSupportedLocale(c);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

// CJK locales render in the system CJK fonts declared in index.css (PingFang,
// Microsoft YaHei/JhengHei, Hiragino, Noto Sans CJK, …). We deliberately do NOT
// fetch a CJK webfont: bundling four multi-megabyte families would bloat the
// app for the 16 Latin locales, and the production CSP (style-src 'self')
// forbids third-party stylesheet hosts — no external font service is ever
// contacted. Latin fonts (Inter, IBM Plex Mono) are self-hosted via @fontsource
// imports in index.css.

/**
 * Apply the locale to the document root: `lang` for accessibility/Intl, a
 * `data-script` attribute that drives the per-locale font stack in index.css,
 * and `dir` so the RTL locales (he/ar/fa) mirror the layout.
 */
export function applyDocumentLocale(code: string): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', code);
  const script: LocaleScript = getLocaleScript(code);
  root.setAttribute('data-script', script);
  root.setAttribute('dir', getLocaleMeta(code)?.dir ?? 'ltr');
}
